import { useState } from 'react';
import { ModalSize, ModalTemplate, modal } from '@ardoq/modal';
import { Select } from '@ardoq/select';
import { TextInput } from '@ardoq/forms';

export type PresentationModalOption = {
  label: string;
  value: string;
  url: string;
  readAccess: string;
  icon?: string;
  iconColor?: string;
  popoverId?: string;
};

export type PresentationDialogLabels = {
  presentationLabel: string;
  presentationSearchPlaceholder: string;
  presentationUrlLabel: string;
  presentationUrlPlaceholder: string;
  linkTextLabel: string;
  linkTextPlaceholder: string;
  linkTextRequiredError: string;
  headerText: string;
  insertButton: string;
  updateButton: string;
  removeButton: string;
  cancelButton: string;
  subtitle: string;
  text: string;
};

export const defaultPresentationDialogLabels: PresentationDialogLabels = {
  presentationLabel: 'Presentation',
  presentationSearchPlaceholder: 'Type to search',
  presentationUrlLabel: 'Presentation URL',
  presentationUrlPlaceholder: 'Presentation URL will be displayed here',
  linkTextLabel: 'Link text',
  linkTextPlaceholder: 'Add link text',
  linkTextRequiredError: 'You must add some text to represent the link',
  headerText: 'Insert Presentation',
  insertButton: 'Insert',
  updateButton: 'Update',
  removeButton: 'Remove link',
  cancelButton: 'Cancel',
  subtitle: 'Select a presentation to link into the message.',
  text: 'Check your presentation’s permissions to make sure it will be accessible to your broadcast audience.',
};

export type PresentationSelectionDialogProps = {
  options: PresentationModalOption[];
  url?: string;
  linkText?: string;
  onClose: VoidFunction;
  onInsert: (args: { linkText: string; url: string }) => void;
  onUpdate: (args: { linkText: string; url: string }) => void;
  onRemove: VoidFunction;
  labels?: PresentationDialogLabels;
};

const PresentationSelectionDialog = ({
  options,
  url,
  linkText,
  onClose,
  onInsert,
  onUpdate,
  onRemove,
  labels = defaultPresentationDialogLabels,
}: PresentationSelectionDialogProps) => {
  const initialSelectedPresentation = options.find(
    option => option.url === url
  );

  const [newLinkText, setNewLinkText] = useState(linkText || '');
  const [newUrl, setNewUrl] = useState(url || '');
  const [selectedPresentation, setSelectedPresentation] = useState(
    initialSelectedPresentation
  );

  const handleUpsert = () => {
    if (!newLinkText || !newUrl) return;
    if (url) {
      onUpdate({
        linkText: newLinkText,
        url: newUrl,
      });
    } else {
      onInsert({
        linkText: newLinkText,
        url: newUrl,
      });
    }
  };

  const handleRemove = () => {
    onRemove();
  };

  return (
    <ModalTemplate
      formContent={
        <>
          <Select
            label={labels.presentationLabel}
            value={selectedPresentation?.value}
            onChange={selectOption => {
              if (!selectOption) return;
              const selectedOption = options.find(
                opt => opt.value === selectOption.value
              );

              if (!selectedOption) return;

              setSelectedPresentation(selectedOption);
              setNewLinkText(selectedOption.label);
              setNewUrl(selectedOption.url);
            }}
            options={options}
            placeholder={labels.presentationSearchPlaceholder}
          />
          <TextInput
            label={labels.presentationUrlLabel}
            isReadOnly
            value={newUrl}
            placeholder={labels.presentationUrlPlaceholder}
          />
          <TextInput
            label={labels.linkTextLabel}
            value={newLinkText}
            placeholder={labels.linkTextPlaceholder}
            onChange={e => setNewLinkText(e.target.value)}
            errorMessage={
              selectedPresentation && !newLinkText.length
                ? labels.linkTextRequiredError
                : undefined
            }
          />
        </>
      }
      headerText={labels.headerText}
      modalSize={ModalSize.S}
      primaryButtonText={url ? labels.updateButton : labels.insertButton}
      onPrimaryButtonClick={handleUpsert}
      isPrimaryButtonDisabled={!newLinkText.length || !newUrl.length}
      onDangerButtonClick={handleRemove}
      dangerButtonText={url ? labels.removeButton : undefined}
      onCloseButtonClick={onClose}
      onSecondaryButtonClick={onClose}
      secondaryButtonText={labels.cancelButton}
      subtitle={labels.subtitle}
      text={labels.text}
    />
  );
};

export const openPresentationSelectionDialog = ({
  linkText,
  url,
  options,
  onRemove,
  onUpdate,
  onInsert,
  labels,
}: Omit<PresentationSelectionDialogProps, 'onClose'>) =>
  modal<{
    newLinkText: string;
    url: string;
  }>(resolve => (
    <PresentationSelectionDialog
      onClose={() => resolve(false)}
      onInsert={args => {
        onInsert(args);
        resolve(false);
      }}
      onRemove={() => {
        onRemove();
        resolve(false);
      }}
      onUpdate={args => {
        onUpdate(args);
        resolve(false);
      }}
      linkText={linkText}
      url={url}
      options={options}
      labels={labels}
    />
  ));
